/**
 * MAP mode: the canonical→source bone mapping draft, its confidence colouring, the world
 * correction and the saved presets. Auto-mapping heuristics and validation live in `src/rig`;
 * the store only holds their results (SPEC "Existing Rigged Model Workflow").
 */

import { normalizeQuat } from '../../shared';
import type { CanonicalBoneId, MappingPreset, QuaternionTuple } from '../../shared';
import type { MappingConfidence, MappingState, MappingValidationInput } from '../types';
import type { ActionContext } from './context';

export interface MappingActions {
  setSourceBones(names: string[]): void;
  setMappingDraft(
    draft: Partial<Record<CanonicalBoneId, string>>,
    confidence?: Partial<Record<CanonicalBoneId, MappingConfidence>>,
  ): void;
  setMappingEntry(canonical: CanonicalBoneId, sourceName: string | null): void;
  setWorldCorrection(q: QuaternionTuple): void;
  setMappingValidation(validation: MappingValidationInput | null): void;
  setPresets(presets: MappingPreset[]): void;
}

export function createMappingActions(ctx: ActionContext): MappingActions {
  const write = (patch: Partial<MappingState>): void => {
    ctx.set({ mapping: { ...ctx.getState().mapping, ...patch } });
  };

  return {
    setSourceBones(names): void {
      write({ sourceBoneNames: [...names] });
    },

    setMappingDraft(draft, confidence): void {
      // Any draft change invalidates the previous validation result.
      write({
        draft: { ...draft },
        confidence: confidence ? { ...confidence } : ctx.getState().mapping.confidence,
        validation: null,
      });
    },

    setMappingEntry(canonical, sourceName): void {
      const mapping = ctx.getState().mapping;
      const previousDraft = mapping.draft;
      const previousConfidence = mapping.confidence;
      const previousValidation = mapping.validation;

      const draft = { ...previousDraft };
      const confidence = { ...previousConfidence };
      if (sourceName === null) {
        delete draft[canonical];
        confidence[canonical] = 'missing';
      } else {
        draft[canonical] = sourceName;
        // A hand-picked bone is by definition a confident mapping.
        confidence[canonical] = 'confident';
      }

      const apply = (): void => write({ draft, confidence, validation: null });
      const revert = (): void =>
        write({
          draft: previousDraft,
          confidence: previousConfidence,
          validation: previousValidation,
        });

      apply();
      ctx.history.push({ label: `Map ${canonical}`, undo: revert, redo: apply });
    },

    setWorldCorrection(q): void {
      write({ worldCorrection: normalizeQuat(q) });
    },

    setMappingValidation(validation): void {
      write({
        validation: validation && { ...validation, warnings: validation.warnings ?? [] },
      });
    },

    setPresets(presets): void {
      write({ presets: [...presets] });
    },
  };
}
